import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiArrowRight, FiCheck, FiStar, FiTruck, FiAward,
  FiClock, FiShield, FiHeart, FiPackage, FiPhone,
  FiChevronLeft, FiChevronRight, FiMail
} from 'react-icons/fi';
import API from '../../utils/api';
import ProductCard from '../common/ProductCard';
import './HomePage.css';

/* ─── Hero (PRESERVED — do not modify layout/grid) ─────────────────── */
export const Hero = () => {
  return (
    <section className="hero">
      <div className="hero-bg">
        <div className="hero-blob blob1" />
        <div className="hero-blob blob2" />
        <div className="hero-grid" />
      </div>
      <div className="container hero-inner">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="pulse-dot" />
            🎨 Premium Print-on-Demand Services
          </div>
          <h1 className="hero-title">
            Ready to Place <br />
            <span className="hero-highlight">Your Order?</span>
          </h1>
          <p className="hero-subtitle">
            To process your order smoothly, kindly register on our website first. Once registered, you can submit your order details and we’ll take care of the rest.
          </p>
          <div className="hero-checks">
            {['Easy & quick registration', 'Fast order processing', 'Reliable delivery'].map(t => (
              <div key={t} className="hero-check"><FiCheck size={14} />{t}</div>
            ))}
          </div>
          <div className="hero-actions">
            <Link to="/login" className="btn btn-primary btn-lg">
              Login Now <FiArrowRight />
            </Link>
            <Link to="/register" className="btn btn-outline btn-lg">
              Register Now
            </Link>
          </div>
          {/* Hero Search */}
          <HeroSearch />
        </div>
        <div className="hero-visual">
          <div className="hero-cards-grid">
            {[
              { img: '/visiting_card.webp', label: 'Visiting Card', price: 'from ₹0.40' },
              { img: '/tshirt.webp', label: 'Custom T-Shirt', price: 'from ₹99' },
              { img: '/bill_board.webp', label: 'Bill Board', price: 'from ₹12/sq ft' },
              { img: '/pamphlet.webp', label: 'Pamphlet', price: 'from ₹0.40' },
            ].map((c, i) => (
              <div key={i} className="hero-card" style={{ animationDelay: `${i * 0.1}s` }}>
                <img src={c.img} alt={c.label} />
                <div className="hero-card-info">
                  <span>{c.label}</span>
                  <span className="hero-card-price">{c.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroSearch = () => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;

    // Log the search (fire-and-forget, never blocks navigation)
    const sessionId = (() => {
      let sid = sessionStorage.getItem('ap_sid');
      if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('ap_sid', sid); }
      return sid;
    })();
    API.post('/catalog/public/search/log', { query: q, resultCount: -1, source: 'hero', sessionId }).catch(() => {});

    navigate(`/products?search=${encodeURIComponent(q)}`);
    setQuery('');
  };

  return (
    <form onSubmit={handleSearch} className="hero-search">
      <FiPackage size={17} className="hero-search-icon" />
      <input
        type="text"
        placeholder="What do you want to print today?"
        value={query}
        onChange={e => setQuery(e.target.value)}
        autoComplete="off"
      />
      <button type="submit" className="hero-search-btn">Search</button>
    </form>
  );
};

/* ─── Trust / Service Benefits ──────────────────────────────────────── */
export const TrustBar = () => (
  <section className="trust-bar">
    <div className="container trust-inner">
      {[
        { icon: <FiAward size={22} />, title: 'Premium Quality', desc: 'High Quality Printing' },
        { icon: <FiTruck size={22} />, title: 'Fast Delivery', desc: 'On Time, Every Time' },
        { icon: <FiShield size={22} />, title: 'Secure Payment', desc: '100% Safe & Secure' },
        { icon: <FiPhone size={22} />, title: '24/7 Support', desc: "We're Here to Help" },
      ].map(t => (
        <div key={t.title} className="trust-item">
          <div className="trust-icon">{t.icon}</div>
          <div className="trust-text">
            <strong>{t.title}</strong>
            <span>{t.desc}</span>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* ─── Stats (kept for internal use if needed) ───────────────────────── */
export const Stats = () => (
  <section className="stats-bar">
    <div className="container stats-inner">
      {[
        { value: '412+', label: 'Custom Products' },
        { value: '50K+', label: 'Happy Customers' },
        { value: '2M+', label: 'Items Delivered' },
        { value: '99.4%', label: 'Satisfaction Rate' },
      ].map(s => (
        <div key={s.label} className="stat-item">
          <div className="stat-value">{s.value}</div>
          <div className="stat-label">{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

/* ─── Features / Why Choose Us (legacy, replaced by WhyChooseUs) ─────── */
export const Features = () => null;

/* ─── Shop by Category ───────────────────────────────────────────────── */
const FALLBACK_CATEGORIES = [
  { id: 1, name: 'Business Cards', slug: 'business-cards', emoji: '🪪' },
  { id: 2, name: 'Flyers', slug: 'flyers', emoji: '📄' },
  { id: 3, name: 'Brochures', slug: 'brochures', emoji: '📋' },
  { id: 4, name: 'Posters', slug: 'posters', emoji: '🖼️' },
  { id: 5, name: 'Stickers', slug: 'stickers', emoji: '🏷️' },
  { id: 6, name: 'Banners', slug: 'banners', emoji: '🎌' },
  { id: 7, name: 'Packaging', slug: 'packaging', emoji: '📦' },
  { id: 8, name: 'Labels', slug: 'labels', emoji: '🔖' },
  { id: 9, name: 'Invitations', slug: 'invitations', emoji: '✉️' },
  { id: 10, name: 'Stationery', slug: 'stationery', emoji: '📝' },
];

export const Categories = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => el.removeEventListener('scroll', handleScroll);
  }, [cats]);

  useEffect(() => {
    API.get('/catalog/public/categories?status=active')
      .then(r => setCats((r.data.categories || []).slice(0, 10)))
      .catch(() => setCats(FALLBACK_CATEGORIES))
      .finally(() => setLoading(false));
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 220, behavior: 'smooth' });
    }
  };

  const displayCats = cats.length > 0 ? cats : FALLBACK_CATEGORIES;

  return (
    <section className="categories-section">
      <div className="container">
        <div className="section-header-row">
          <div>
            <h2 className="section-title">Shop by Category</h2>
          </div>
          <Link to="/products" className="view-all-link">View All Categories <FiArrowRight size={14} /></Link>
        </div>
        <div className="cats-scroll-wrapper">
          {showLeft && (
            <button className="cats-scroll-btn left" onClick={() => scroll(-1)} aria-label="Scroll left">
              <FiChevronLeft size={18} />
            </button>
          )}
          <div className="cats-scroll" ref={scrollRef}>
            {loading
              ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="cat-card skeleton" />)
              : displayCats.map(c => (
                <Link key={c.id || c.slug} to={`/${c.slug}`} className="cat-card">
                  <div className="cat-img-wrap">
                    {c.image
                      ? <img src={c.image} alt={c.name} loading="lazy" />
                      : <div className="cat-emoji">{c.emoji || '🖨️'}</div>
                    }
                  </div>
                  <span className="cat-name">{c.name}</span>
                </Link>
              ))
            }
          </div>
          {showRight && (
            <button className="cats-scroll-btn right" onClick={() => scroll(1)} aria-label="Scroll right">
              <FiChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

/* ─── Popular Products ───────────────────────────────────────────────── */
const FALLBACK_PRODUCTS = [
  { id: 1, name: 'Premium Business Cards', basePrice: 199, spec: '100 pcs · 350 GSM · Matte', slug: 'business-cards' },
  { id: 2, name: 'A4 Flyers', basePrice: 299, spec: '100 pcs · 130 GSM', slug: 'flyers' },
  { id: 3, name: 'Tri-Fold Brochure', basePrice: 499, spec: '10 pcs · Premium Paper', slug: 'brochures' },
  { id: 4, name: 'Vinyl Stickers', basePrice: 199, spec: '10 pcs · Waterproof', slug: 'stickers' },
  { id: 5, name: 'Roll Up Banner', basePrice: 999, spec: '1 pc · Premium Quality', slug: 'banners' },
];

export const FeaturedProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState({});

  useEffect(() => {
    API.get('/catalog/public/products?featured=true&status=active&limit=8')
      .then(r => setProducts(r.data.products || []))
      .catch(() => setProducts(FALLBACK_PRODUCTS))
      .finally(() => setLoading(false));
  }, []);

  const toggleWish = (id) => setWishlist(w => ({ ...w, [id]: !w[id] }));

  return (
    <section className="popular-products">
      <div className="container">
        <div className="section-header-row">
          <div>
            <h2 className="section-title">Popular Products</h2>
          </div>
          <Link to="/products" className="view-all-link">View All Products <FiArrowRight size={14} /></Link>
        </div>
        {loading ? (
          <div className="products-grid">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="product-skeleton" />)}
          </div>
        ) : (
          <div className="products-grid">
            {products.length > 0
              ? products.map(p => <ProductCard key={p.id} product={p} />)
              : FALLBACK_PRODUCTS.map(p => (
                <div key={p.id} className="pp-card">
                  <div className="pp-img-wrap">
                    <div className="pp-img-placeholder">🖨️</div>
                    <button
                      className={`pp-wish-btn ${wishlist[p.id] ? 'active' : ''}`}
                      onClick={() => toggleWish(p.id)}
                      aria-label="Add to wishlist"
                    >
                      <FiHeart size={16} />
                    </button>
                  </div>
                  <div className="pp-info">
                    <h3 className="pp-name">{p.name}</h3>
                    <p className="pp-price">Starting at ₹{p.basePrice}</p>
                    <p className="pp-spec">{p.spec}</p>
                    <Link to={`/${p.slug}`} className="btn btn-primary pp-btn">
                      Customize Now <FiArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>
    </section>
  );
};

/* ─── How It Works ───────────────────────────────────────────────────── */
export const HowItWorks = () => (
  <section className="how-it-works">
    <div className="container">
      <div className="section-header-center">
        <h2 className="section-title">How It Works</h2>
        <p className="section-sub">From design to delivery in just a few simple steps.</p>
      </div>
      <div className="steps-row">
        {[
          { num: '01', icon: '🖱️', title: 'Choose Product', desc: 'Select the printing product you need from our wide range.' },
          { num: '02', icon: '🎨', title: 'Customize & Upload', desc: 'Select options and upload your artwork or design.' },
          { num: '03', icon: '🛒', title: 'Review & Order', desc: 'Review your requirements and confirm your order.' },
          { num: '04', icon: '🚚', title: 'We Print & Deliver', desc: 'We produce and deliver your order fast and securely.' },
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div className="step-item">
              <div className="step-circle">
                <span className="step-emoji">{s.icon}</span>
                <span className="step-num-badge">{s.num}</span>
              </div>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-desc">{s.desc}</p>
            </div>
            {i < 3 && <div className="step-arrow"><FiArrowRight size={20} /></div>}
          </React.Fragment>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Our Clients ───────────────────────────────────────────────────── */
export const OurClients = () => {
  const scrollRef = useRef(null);
  const [logos, setLogos] = useState([]);
  const [loadingLogos, setLoadingLogos] = useState(true);

  useEffect(() => {
    API.get('/client-logos/public')
      .then(({ data }) => setLogos(data.logos || []))
      .catch(() => setLogos([]))
      .finally(() => setLoadingLogos(false));
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  // Don't render the section at all if there are no logos
  if (!loadingLogos && logos.length === 0) return null;

  return (
    <section className="clients-section">
      <div className="container">
        <div className="section-header-center">
          <h2 className="section-title">Our Clients</h2>
          <p className="section-sub">Trusted by Businesses &amp; Brands across India</p>
        </div>
        <div className="clients-carousel-wrapper">
          <button className="clients-nav-btn" onClick={() => scroll(-1)} aria-label="Previous">
            <FiChevronLeft size={18} />
          </button>
          <div className="clients-scroll" ref={scrollRef}>
            {loadingLogos
              ? /* Skeleton placeholders while loading */
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="client-logo-card" style={{ background: '#f3f4f6', border: '1px solid #e5e7eb' }}>
                    <div style={{ width: 80, height: 24, borderRadius: 4, background: '#e5e7eb' }} />
                  </div>
                ))
              : logos.map(logo => (
                  <div key={logo.id} className="client-logo-card">
                    <img
                      src={logo.logoUrl}
                      alt={logo.name}
                      className="client-logo-img"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ))
            }
          </div>
          <button className="clients-nav-btn" onClick={() => scroll(1)} aria-label="Next">
            <FiChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── Why Choose Us ─────────────────────────────────────────────────── */
export const WhyChooseUs = () => (
  <section className="why-choose-us">
    <div className="container">
      <div className="section-header-center">
        <h2 className="section-title">What Our Customers Say</h2>
        <p className="section-sub">Don't just take our word for it.</p>
      </div>
      <div className="why-grid">
        {[
          { icon: <FiAward size={24} />, title: 'Premium Printing Quality', desc: 'Vibrant colours, crisp detail and professional finishes on every order.' },
          { icon: '₹', title: 'Competitive Pricing', desc: 'Best prices in the market without compromising on quality.' },
          { icon: <FiClock size={24} />, title: 'Fast Turnaround', desc: 'Rush printing available. Same-day processing for orders placed before noon.' },
          { icon: '🎨', title: 'Multiple Customization Options', desc: 'From sizes to materials — customise every detail your way.' },
          { icon: <FiTruck size={24} />, title: 'Reliable Delivery', desc: 'Pan-India delivery with real-time tracking.' },
          { icon: <FiPhone size={24} />, title: 'Dedicated Support', desc: 'Our team is available 6 days a week to help you.' },
        ].map(w => (
          <div key={w.title} className="why-card">
            <div className="why-icon">
              {typeof w.icon === 'string' ? <span>{w.icon}</span> : w.icon}
            </div>
            <h3>{w.title}</h3>
            <p>{w.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Bulk Quote CTA ────────────────────────────────────────────────── */
export const BulkQuoteCTA = () => (
  <section className="bulk-cta">
    <div className="container">
      <div className="bulk-cta-inner">
        <div className="bulk-cta-blob" />
        <div className="bulk-cta-content">
          <div className="bulk-cta-badge">✨ Custom &amp; Bulk Orders</div>
          <h2>Have a Unique Printing Requirement?</h2>
          <p>Need bulk printing, custom dimensions, special materials, or something entirely different? We've got you covered.</p>
          <Link to="/contact?type=quote" className="btn btn-primary btn-lg">
            Request a Quote <FiArrowRight />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Testimonials ───────────────────────────────────────────────────── */
export const Testimonials = () => {
  const reviews = [
    { name: 'Rohit Sharma', role: 'Marketing Head', text: 'Excellent print quality and fast delivery. Highly recommended!', rating: 5, avatar: 'R' },
    { name: 'Priya Mehta', role: 'Business Owner', text: 'Superb service and customer support. Will order again!', rating: 5, avatar: 'P' },
    { name: 'Ankit Verma', role: 'Startup Founder', text: 'Best printing experience for my business needs.', rating: 5, avatar: 'A' },
    { name: 'Sarah Mitchell', role: 'Marketing Director', text: 'The business cards we ordered blew our client away. Exceptional quality!', rating: 5, avatar: 'S' },
    { name: 'David Okafor', role: 'Graphic Designer', text: 'Reliable, affordable and colour accuracy is spot on every time.', rating: 5, avatar: 'D' },
    { name: 'Tom Nguyen', role: 'Event Organiser', text: 'Ordered 500 flyers — stunning quality, vivid colours. Exactly what we needed!', rating: 5, avatar: 'T' },
  ];

  const [active, setActive] = useState(0);

  return (
    <section className="testimonials">
      <div className="container">
        <div className="section-header-center">
          <h2 className="section-title">What Our Customers Say</h2>
          <p className="section-sub">Loved by thousands of businesses across India.</p>
        </div>
        <div className="testimonials-grid">
          {reviews.map((t, i) => (
            <div key={t.name} className={`testimonial-card ${i === active ? 'active' : ''}`}>
              <div className="test-stars">
                {Array.from({ length: t.rating }).map((_, j) => <FiStar key={j} className="star" size={15} />)}
              </div>
              <p className="test-text">"{t.text}"</p>
              <div className="test-author">
                <div className="test-avatar">{t.avatar}</div>
                <div>
                  <div className="test-name">{t.name}</div>
                  <div className="test-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="test-dots">
          {Array.from({ length: Math.ceil(reviews.length / 3) }).map((_, i) => (
            <button key={i} className={`test-dot ${Math.floor(active / 3) === i ? 'active' : ''}`} onClick={() => setActive(i * 3)} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Newsletter ─────────────────────────────────────────────────────── */
// export const Newsletter = () => {
//   const [email, setEmail] = useState('');
//   const [sent, setSent] = useState(false);

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (email.trim()) { setSent(true); setEmail(''); }
//   };

//   return (
//     <section className="newsletter">
//       <div className="container">
//         <div className="newsletter-inner">
//           <div className="newsletter-text">
//             <h3>Stay Updated</h3>
//             <p>Get printing tips, exclusive offers and new product launches.</p>
//           </div>
//           {sent ? (
//             <div className="newsletter-success">
//               <FiCheck size={20} /> Thanks for subscribing!
//             </div>
//           ) : (
//             <form onSubmit={handleSubmit} className="newsletter-form">
//               <div className="newsletter-input-wrap">
//                 <FiMail size={17} className="newsletter-icon" />
//                 <input
//                   type="email"
//                   placeholder="Enter your email"
//                   value={email}
//                   onChange={e => setEmail(e.target.value)}
//                   required
//                 />
//               </div>
//               <button type="submit" className="btn btn-primary">Subscribe</button>
//             </form>
//           )}
//         </div>
//       </div>
//     </section>
//   );
// };

/* ─── CTA (legacy, kept for compatibility) ────────────────────────────── */
export const CTA = () => null;